<template>
  <v-card flat class="cc-input-form">
    <v-container class="pa-6">
      <v-row v-if="showCCHeader">
        <v-col>
          <div class="d-flex flex-column">
            <div class="d-flex justify-space-between">
              <div>
                <v-icon> $creditCardIcon </v-icon>
                {{ titleText }}
              </div>
            </div>
            <div class="subtitle">
              {{ subTitleText }}
            </div>
          </div>
        </v-col>
      </v-row>
      <v-fade-transition>
        <v-row v-if="cardError || addCardError">
          <v-col>
            <v-alert color="secondary">
              <template v-slot:prepend>
                <v-icon color="white" class="mr-2">mdi-alert</v-icon>
              </template>
              {{ computedCardErrorMessage }}
            </v-alert>
          </v-col>
        </v-row>
      </v-fade-transition>
      <v-row>
        <v-col cols="8">
          <v-progress-circular
            :size="120"
            indeterminate
            v-if="localChargifyLoading"
            class="chargify-progress"
          />
        </v-col>
      </v-row>
      <v-row
        id="chargify-form"
        :class="{ 'hide-chargify-form': localChargifyLoading }"
      >
        <v-col cols="8" class="pr-6">
          <div class="d-flex flex-column">
            <div class="mb-3">{{ $t('dealer.chargify.cardDetails') }}</div>

            <div class="d-flex">
              <div class="mr-2">
                <div class="input-label">
                  <span class="caption font-weight-light">
                    {{ $t('admin.formFields.firstName') }}
                  </span>
                  <span class="required-field-asterisk">*</span>
                </div>
                <div id="first_name" />
              </div>
              <div>
                <div class="input-label">
                  <span class="caption font-weight-light">
                    {{ $t('admin.formFields.lastName') }}
                  </span>
                  <span class="required-field-asterisk">*</span>
                </div>
                <div id="last_name" />
              </div>
            </div>
            <div>
              <div class="input-label">
                <span class="caption font-weight-light">
                  {{ $t('dealer.chargify.cardNumber') }}
                </span>
                <span class="required-field-asterisk">*</span>
              </div>
              <div id="cc_number" />
            </div>
            <div class="d-flex">
              <div>
                <div class="input-label">
                  <span class="caption font-weight-light">
                    {{ $t('dealer.chargify.ccFormFields.month') }}
                  </span>
                  <span class="required-field-asterisk">*</span>
                </div>
                <div id="cc_month" />
              </div>
              <div>
                <div class="input-label">
                  <span class="caption font-weight-light">
                    {{ $t('dealer.chargify.ccFormFields.year') }}
                  </span>
                  <span class="required-field-asterisk">*</span>
                </div>
                <div id="cc_year" />
              </div>
              <div>
                <div class="input-label">
                  <span class="caption font-weight-light">
                    {{ $t('dealer.chargify.cvv') }}
                  </span>
                  <span class="required-field-asterisk">*</span>
                </div>
                <div id="cc_cvv" />
              </div>
            </div>
            <div class="d-flex">
              <div class="mr-2">
                <div class="input-label">
                  <span class="caption font-weight-light">
                    {{ $t('admin.formFields.country') }}</span
                  >
                  <span class="required-field-asterisk">*</span>
                </div>
                <div id="billing_country" />
              </div>
              <div class="">
                <div class="input-label">
                  <span class="caption font-weight-light">
                    {{ $t('dealer.chargify.ccFormFields.zipOrPostal') }}</span
                  >
                </div>
                <div id="billing_zip" />
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
      <v-fade-transition>
        <v-row v-if="!localChargifyLoading && showCCButtons">
          <v-col cols="8">
            <div class="d-flex justify-end">
              <v-btn
                @click="decline"
                class="mr-2"
                :loading="localLogoutLoading"
              >
                <template v-if="showCloseButton">
                  {{ $t('global.cancel') }}
                </template>
                <template v-else>
                  {{ $t('dealer.chargify.declineAndLogout') }}
                </template>
              </v-btn>
              <v-btn
                @click="addPaymentClick"
                color="secondary"
                v-if="dealerAdminOnly"
                :loading="paymentLoading"
              >
                {{ $t('dealer.chargify.addPaymentMethod') }}
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-fade-transition>
    </v-container>
  </v-card>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { mapActions, mapState } from 'vuex'
import chargify from '@/mixins/chargify'

export default defineComponent({
  name: 'CCEntryForm',
  mixins: [chargify],
  props: {
    titleText: {
      type: String,
      default: '',
    },
    subTitleText: {
      type: String,
      default: '',
    },
    fullscreenPromptView: {
      type: Boolean,
      default: false,
    },
    localLogoutLoading: {
      type: Boolean,
      default: false,
    },
    showCloseButton: {
      type: Boolean,
      default: false,
    },
    showCCButtons: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      cardError: false,
      cardErrorMessage: '',
      localChargifyLoading: false,
      paymentLoading: false,
    }
  },
  computed: {
    ...mapState('Payment', ['addCardError', 'addCardErrorMessage']),
    ccTitleText() {
      return this.$t('dealer.chargify.addPayment')
    },
    dealerAdminOnly() {
      return true
    },
    computedCardErrorMessage() {
      if (this.cardErrorMessage == '') {
        return this.addCardErrorMessage
      } else {
        if (Array.isArray(this.cardErrorMessage)) {
          let messageItem = this.cardErrorMessage[0]
          return messageItem.replace(':', '')
        } else {
          return this.cardErrorMessage
        }
      }
    },
    showCCHeader() {
      if (this.titleText == '' && this.subTitleText == '') {
        return false
      } else {
        return true
      }
    },
  },
  methods: {
    ...mapActions('Payment', [
      'addNewCard',
      'resetCardError',
      'callAddPaymentLoading',
    ]),
    ...mapActions('ToastMessage', ['callSuccessToast']),
    decline() {
      if (this.showCloseButton) {
        this.closeDialog()
      } else {
        this.$emit('decline')
      }
    },
    async addPaymentClick() {
      this.paymentLoading = true
      this.callAddPaymentLoading(this.paymentLoading)
      this.cardErrorMessage = ''
      this.cardError = false
      try {
        const token = await this.submitCardInfo('chargify-form')
        let payload = {
          chargify_token: token,
        }
        await this.addNewCard(payload)
        if (!this.addCardError) {
          this.closeDialog()
          this.callSuccessToast({
            toastMessage: this.$t('dealer.chargify.paymentMethodAdded'),
          })
        }
        this.paymentLoading = false
        this.callAddPaymentLoading(this.paymentLoading)
      } catch (err) {
        if (err.errors) {
          this.cardError = true
          this.cardErrorMessage = err.errors
        }
        this.paymentLoading = false
        this.callAddPaymentLoading(this.paymentLoading)
      }
    },
    closeDialog() {
      this.$emit('closeDialog')
      this.chargify.unload()
    },
    reloadCard() {
      this.cardErrorMessage = ''
      this.cardError = false
      this.resetCardError()
      this.chargify.unload()
      this.loadChargifyWithLoading()
    },
    loadChargifyWithLoading() {
      this.localChargifyLoading = true
      this.loadChargify('card')
      setTimeout(() => {
        this.localChargifyLoading = false
      }, 2000)
    },
  },
  mounted() {
    this.loadChargifyWithLoading()
    this.resetCardError()
  },
})
</script>

<style lang="scss">
.cc-input-form {
  overflow: hidden;
  .chargify-progress {
    position: absolute;
    top: 150px;
    left: 200px;
  }
  .v-input {
    font-size: 14px;
  }
  .v-text-field.v-text-field--enclosed .v-text-field__details {
    margin-bottom: 0px !important;
  }
  .v-icon__svg {
    fill: black;
  }

  .v-text-field .v-input__control .v-input__slot {
    font-size: 14px;
    min-height: 20px !important;
    margin-bottom: 4px !important;
  }
  .v-text-field--box .v-input__slot,
  .v-text-field--outline .v-input__slot {
    min-height: 20px !important;
    margin-bottom: 4px !important;
  }
  .vue-tel-input-vuetify {
    align-items: flex-start;
    .v-input {
      border-radius: 2px !important;
    }
    .country-code {
      .v-text-field__details {
        display: none;
      }
      .v-input__slot {
        padding: 0;
      }
      .vti__flag {
        margin-left: 10px !important;
      }
      .v-input__control {
        padding-left: 0px;
        margin-right: 8px;
      }
      .v-input__append-inner {
        margin-left: 24px;
        margin-top: auto;
      }
    }
    .v-select__slot {
      height: 32px;
      margin-right: 4px;
    }
    input {
      color: black !important;
      caret-color: black !important;
    }
    .v-input__slot::before {
      display: none;
    }
    .v-input__slot::after {
      display: none;
    }
  }
  #first_name iframe,
  #last_name iframe {
    width: 100% !important;
  }
  #last_name iframe {
  }

  #cc_number iframe {
    width: 100% !important;
    max-height: 65px;
  }
  #cc_month iframe {
    width: 90% !important;
    max-height: 65px;
  }
  #cc_year iframe {
    width: 90% !important;
    max-height: 65px;
  }
  #cc_cvv iframe {
    width: 100% !important;
    max-height: 65px;
  }
  #billing_zip iframe {
    width: 100% !important;
    max-height: 65px;
  }
  #billing_country iframe {
    width: 100% !important;
    cursor: pointer;
    overflow: hidden;
    max-height: 65px;
    min-height: 61px;
  }
  #chargify-form {
    visibility: visible;
    opacity: 1;
    transition: visibility 0s, opacity 0.5s linear;
  }
  .hide-chargify-form {
    transition: visibility 0s, opacity 0.5s linear !important;
    visibility: hidden !important;
    opacity: 0 !important;
  }
}
</style>
